import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { ISnowDCCSState } from './snowDCCS.model';
import {
  deleteSnowDCCS,
  getSnowDCCSById,
  saveSnowDCCS,
  searchSnowDCCS,
} from './snowDCCS.action';
import { ISnowDCCS } from '../../../services/snow/snowDCCS/snowDCCS.model';

export const initialState: ISnowDCCSState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const snowDCCSSlice = createSlice({
  name: 'snowDCCS',
  initialState,
  reducers: {
    clearSnowDCCS: () => {
      return initialState;
    },
    clearSnowDCCSMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearSnowDCCSGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchSnowDCCS.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchSnowDCCS.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchSnowDCCS.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getSnowDCCSById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getSnowDCCSById.fulfilled.type]: (
      state,
      action: PayloadAction<ISnowDCCS>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getSnowDCCSById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveSnowDCCS.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveSnowDCCS.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveSnowDCCS.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteSnowDCCS.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteSnowDCCS.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteSnowDCCS.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const snowDCCSSelector = (state: RootState) => state.snowDCCS;

// Actions
export const {
  clearSnowDCCS,
  clearSnowDCCSMessages,
  clearSnowDCCSGetById,
  setTableColumnSelection,
} = snowDCCSSlice.actions;

// The reducer
export default snowDCCSSlice.reducer;
